import React from "react";

import Info from "./Info";

import EditCard from "uicomponents/EditCard";
import { ConvertToValidDate } from "utils/helper";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";

import {
  EmployeeCensusesList,
  EmployeeCompanyInformationType,
  EmployeesWhoMakeDeliveriesInputType,
  EmployeesWhoPerformHeavyLiftingInputType,
  EmployeesWhoRequirePhysicalLaborInputType,
  EmployeesWhoTravelInputType,
  EmployeesWhoUseToolsInputType,
  EmployeesWhoWorkOnLaddersInputType,
  EmployeesWhoWorkUndergroundInputType,
  HasEmployeeType,
  UnderageOrSeniorEmployeesInputType,
} from "types";

import styles from "../ReviewAndSubmit.module.css";

interface IEmployeesAttributesReviewProps {
  data: EmployeeCompanyInformationType;
  setActiveStepIndex: (index: number) => void;
  sectionNumber: number;
  employeeCensus: EmployeeCensusesList;
}

type TDataEntries = [
  keyof EmployeeCompanyInformationType & HasEmployeeType,
  string
];

const EmployeesAttributesReview = ({
  data,
  setActiveStepIndex,
  sectionNumber,
  employeeCensus,
}: IEmployeesAttributesReviewProps): JSX.Element => {
  if (!data?.hasEmployees || !data?.bizActivities) {
    return <React.Fragment key="no-data-found"></React.Fragment>;
  }

  const questions = Object.entries(
    sectionsQuestions.employeesAttributes.questions
  ) as TDataEntries[];

  const {
    employeesWhoTravel,
    employeesWhoUseTools,
    employeesWhoRequirePhysicalLabor,
    employeesWhoWorkUnderground,
    employeesWhoWorkOnLadders,
    employeesWhoPerformHeavyLifting,
    employeesWhoMakeDeliveries,
    underageOrSeniorEmployees,
  } = sectionsQuestions.employeesAttributes.questions;

  return (
    <EditCard
      title={`${sectionsQuestions.employeesAttributes.title}`}
      onEditClick={() => setActiveStepIndex(sectionNumber)}
    >
      {questions.map(([key, value]) => {
        if (key === "employeesWhoTravel") {
          const whoTravelQuestions = Object.entries(employeesWhoTravel) as [
            keyof EmployeesWhoTravelInputType,
            string
          ][];

          const hasSomeEmployeeWhoTravel =
            !!data.hasEmployees?.employeesWhoTravel?.[0]?.employeeId;

          if (!hasSomeEmployeeWhoTravel)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoTravel?.map(
            (whoTravel, index) => {
              return (
                <React.Fragment key={`whoTravel-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Travels
                  </span>

                  {whoTravelQuestions.map(([whoTravelKey, whoTravelValue]) => {
                    if (whoTravelKey === "employeeId") {
                      const employee = employeeCensus?.find(
                        (employee) => employee.uuid === whoTravel[whoTravelKey]
                      );
                      return (
                        <Info
                          key={`${whoTravelKey}-${key}-${index}`}
                          question="Full name"
                          answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                          questionFieldName={key}
                        />
                      );
                    }

                    return (
                      <Info
                        key={`${whoTravelKey}-${key}-${index}`}
                        question={whoTravelValue}
                        answer={whoTravel[whoTravelKey]}
                        questionFieldName={key}
                      />
                    );
                  })}

                  <hr className={styles.subsectionEnd} />
                </React.Fragment>
              );
            }
          );
        }

        if (key === "employeesWhoUseTools") {
          const whoUseToolsQuestions = Object.entries(employeesWhoUseTools) as [
            keyof EmployeesWhoUseToolsInputType,
            string
          ][];

          const hasSomeEmployeeWhoUseTools =
            !!data.hasEmployees?.employeesWhoUseTools?.[0]?.employeeId;

          if (!hasSomeEmployeeWhoUseTools)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoUseTools?.map(
            (whoUseTools, index) => {
              return (
                <React.Fragment key={`whoUseTools-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Uses Tools
                  </span>

                  {whoUseToolsQuestions.map(
                    ([whoUseToolsKey, whoUseToolsValue]) => {
                      if (whoUseToolsKey === "employeeId") {
                        const employee = employeeCensus?.find(
                          (employee) =>
                            employee.uuid === whoUseTools[whoUseToolsKey]
                        );

                        return (
                          <Info
                            key={`${whoUseToolsKey}-${key}-${index}`}
                            question="Full name"
                            answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                            questionFieldName={key}
                          />
                        );
                      }

                      return (
                        <Info
                          key={`${whoUseToolsKey}-${key}-${index}`}
                          question={whoUseToolsValue}
                          answer={whoUseTools[whoUseToolsKey]}
                          questionFieldName={key}
                        />
                      );
                    }
                  )}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "employeesWhoRequirePhysicalLabor") {
          const physicalLaborQuestions = Object.entries(
            employeesWhoRequirePhysicalLabor
          ) as [keyof EmployeesWhoRequirePhysicalLaborInputType, string][];

          const hasPhysicalLaborEmployees =
            !!data.hasEmployees?.employeesWhoRequirePhysicalLabor?.[0]
              ?.employeeId;

          if (!hasPhysicalLaborEmployees)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoRequirePhysicalLabor?.map(
            (physicalLabor, index) => {
              return (
                <React.Fragment
                  key={`employeesWhoRequirePhysicalLabor-${index}`}
                >
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Requires Physical Labor
                  </span>

                  {physicalLaborQuestions.map(
                    ([physicalLaborKey, physicalLaborValue]) => {
                      if (physicalLaborKey === "employeeId") {
                        const employee = employeeCensus?.find(
                          (employee) =>
                            employee.uuid === physicalLabor[physicalLaborKey]
                        );

                        return (
                          <Info
                            key={`${physicalLaborKey}-${key}-${index}`}
                            question="Full name"
                            answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                            questionFieldName={key}
                          />
                        );
                      }

                      return (
                        <Info
                          key={`${physicalLaborKey}-${key}-${index}`}
                          question={physicalLaborValue}
                          answer={physicalLabor[physicalLaborKey]}
                          questionFieldName={key}
                        />
                      );
                    }
                  )}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "boatsBridges") {
          const hasBoatsBridgesEmployees =
            !!data.hasEmployees?.employeesWhoUseBoatsBridges?.[0];

          if (!hasBoatsBridgesEmployees) {
            return <React.Fragment key={key}></React.Fragment>;
          }

          return (
            <>
              <Info
                key={key}
                question={value}
                answer={data?.hasEmployees?.[key] ?? data?.bizActivities?.[key]}
                questionFieldName={key}
              />
              {data.hasEmployees?.employeesWhoUseBoatsBridges?.map(
                (boatsBridgesEmployee, index) => {
                  const employee = employeeCensus?.find(
                    (employee) => employee.uuid === boatsBridgesEmployee
                  );

                  return (
                    <React.Fragment
                      key={`employeesWhoUseBoatsBridges-${index}`}
                    >
                      <span className={styles.subsectionTitle}>
                        {index + 1} : Teammate Who Uses Boats or Bridges
                      </span>
                      <Info
                        key={`${key}-${index}`}
                        question="Full name"
                        answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                        questionFieldName={key}
                      />
                    </React.Fragment>
                  );
                }
              )}
            </>
          );
        }

        if (key === "employeesWhoWorkUnderground") {
          const undergroundQuestions = Object.entries(
            employeesWhoWorkUnderground
          ) as [keyof EmployeesWhoWorkUndergroundInputType, string][];

          const hasUndergroundEmployees =
            !!data.hasEmployees?.employeesWhoWorkUnderground?.[0]?.employeeId;

          if (!hasUndergroundEmployees)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoWorkUnderground?.map(
            (whoWorksUnderground, index) => {
              return (
                <React.Fragment key={`whoWorksUnderground-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Works Underground
                  </span>

                  {undergroundQuestions.map(
                    ([undergroundKey, undergroundValue]) => {
                      if (undergroundKey === "employeeId") {
                        const employee = employeeCensus?.find(
                          (employee) =>
                            employee.uuid ===
                            whoWorksUnderground[undergroundKey]
                        );

                        return (
                          <Info
                            key={`${undergroundKey}-${key}-${index}`}
                            question="Full name"
                            answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                            questionFieldName={key}
                          />
                        );
                      }

                      return (
                        <Info
                          key={`${undergroundKey}-${key}-${index}`}
                          question={undergroundValue}
                          answer={whoWorksUnderground[undergroundKey]}
                          questionFieldName={key}
                        />
                      );
                    }
                  )}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "employeesWhoWorkOnLadders") {
          const ladderQuestions = Object.entries(employeesWhoWorkOnLadders) as [
            keyof EmployeesWhoWorkOnLaddersInputType,
            string
          ][];

          const hasEmployeesWhoWorkOnLadders =
            !!data.hasEmployees?.employeesWhoWorkOnLadders?.[0]?.employeeId;

          if (!hasEmployeesWhoWorkOnLadders)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoWorkOnLadders?.map(
            (whoWorksLadders, index) => {
              return (
                <React.Fragment key={`whoWorksLadders-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Works On Ladders
                  </span>

                  {ladderQuestions.map(([laddersKey, laddersValue]) => {
                    if (laddersKey === "employeeId") {
                      const employee = employeeCensus?.find(
                        (employee) =>
                          employee.uuid === whoWorksLadders[laddersKey]
                      );
                      return (
                        <Info
                          key={`${laddersKey}-${key}-${index}`}
                          question="Full name"
                          answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                          questionFieldName={key}
                        />
                      );
                    }

                    return (
                      <Info
                        key={`${laddersKey}-${key}-${index}`}
                        question={laddersValue}
                        answer={whoWorksLadders[laddersKey]}
                        questionFieldName={key}
                      />
                    );
                  })}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "employeesWhoPerformHeavyLifting") {
          const heavyLiftingQuestions = Object.entries(
            employeesWhoPerformHeavyLifting
          ) as [keyof EmployeesWhoPerformHeavyLiftingInputType, string][];

          const hasHeavyLiftingEmployees =
            !!data.hasEmployees?.employeesWhoPerformHeavyLifting?.[0]
              ?.employeeId;

          if (!hasHeavyLiftingEmployees)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.employeesWhoPerformHeavyLifting?.map(
            (whoHeavyLifts, index) => {
              return (
                <React.Fragment key={`whoHeavyLifts-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Performs Heavy Lifting
                  </span>

                  {heavyLiftingQuestions.map(
                    ([heavyLiftingKey, heavyLiftingValue]) => {
                      if (heavyLiftingKey === "employeeId") {
                        const employee = employeeCensus?.find(
                          (employee) =>
                            employee.uuid === whoHeavyLifts[heavyLiftingKey]
                        );
                        return (
                          <Info
                            key={`${heavyLiftingKey}-${key}-${index}`}
                            question="Full name"
                            answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                            questionFieldName={key}
                          />
                        );
                      }

                      return (
                        <Info
                          key={`${heavyLiftingKey}-${key}-${index}`}
                          question={heavyLiftingValue}
                          answer={whoHeavyLifts[heavyLiftingKey]}
                          questionFieldName={key}
                        />
                      );
                    }
                  )}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "employeesWhoMakeDeliveries") {
          const deliveriesQuestions = Object.entries(
            employeesWhoMakeDeliveries
          ) as [keyof EmployeesWhoMakeDeliveriesInputType, string][];

          const hasEmployeesWhoMakeDeliveries =
            !!data.bizActivities?.employeesWhoMakeDeliveries?.[0]?.employeeId;

          if (!hasEmployeesWhoMakeDeliveries)
            return <React.Fragment key={key}></React.Fragment>;

          return data.bizActivities?.employeesWhoMakeDeliveries?.map(
            (whoMakesDeliveries, index) => {
              return (
                <React.Fragment key={`whoMakesDeliveries-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Teammate Who Makes Deliveries
                  </span>

                  {deliveriesQuestions.map(([deliveryKey, deliveryValue]) => {
                    if (deliveryKey === "employeeId") {
                      const employee = employeeCensus?.find(
                        (employee) =>
                          employee.uuid === whoMakesDeliveries[deliveryKey]
                      );
                      return (
                        <Info
                          key={`${deliveryKey}-${key}-${index}`}
                          question="Full name"
                          answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                          questionFieldName={key}
                        />
                      );
                    }

                    return (
                      <Info
                        key={`${deliveryKey}-${key}-${index}`}
                        question={deliveryValue}
                        answer={whoMakesDeliveries[deliveryKey]}
                        questionFieldName={key}
                      />
                    );
                  })}
                </React.Fragment>
              );
            }
          );
        }

        if (key === "physicalDisability") {
          const hasPhysicalDisabilityEmployees =
            !!data.hasEmployees?.employeesWhoHavePhysicalDisabilities?.[0];

          if (!hasPhysicalDisabilityEmployees) {
            return <React.Fragment key={key}></React.Fragment>;
          }

          return (
            <>
              <Info
                key={key}
                question={value}
                answer={data?.hasEmployees?.[key] ?? data?.bizActivities?.[key]}
                questionFieldName={key}
              />
              {data.hasEmployees?.employeesWhoHavePhysicalDisabilities?.map(
                (physicalDisabilityEmployee, index) => {
                  const employee = employeeCensus?.find(
                    (employee) => employee.uuid === physicalDisabilityEmployee
                  );

                  return (
                    <React.Fragment
                      key={`employeesWhoHavePhysicalDisabilities-${index}`}
                    >
                      <span className={styles.subsectionTitle}>
                        {index + 1} : Teammate Who Has Physical Disabilities
                      </span>
                      <Info
                        key={`${key}-${index}`}
                        question="Full name"
                        answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                        questionFieldName={key}
                      />
                    </React.Fragment>
                  );
                }
              )}
            </>
          );
        }

        if (key === "underageOrSeniorEmployees") {
          const underageOrSeniorQuestions = Object.entries(
            underageOrSeniorEmployees
          ) as [keyof UnderageOrSeniorEmployeesInputType, string][];

          const hasSomeUnderageOrSeniorEmployees =
            !!data.hasEmployees?.underageOrSeniorEmployees?.[0]?.employeeId;

          if (!hasSomeUnderageOrSeniorEmployees)
            return <React.Fragment key={key}></React.Fragment>;

          return data.hasEmployees?.underageOrSeniorEmployees?.map(
            (underageOrSenior, index) => {
              return (
                <React.Fragment key={`underageOrSeniorEmployees-${index}`}>
                  <span className={styles.subsectionTitle}>
                    {index + 1} : Underage or Senior Teammate
                  </span>

                  {underageOrSeniorQuestions.map(
                    ([underageOrSeniorKey, underageOrSeniorValue]) => {
                      if (underageOrSeniorKey === "employeeId") {
                        const employee = employeeCensus?.find(
                          (employee) =>
                            employee.uuid ===
                            underageOrSenior[underageOrSeniorKey]
                        );

                        return (
                          <Info
                            key={`${underageOrSeniorKey}-${key}-${index}`}
                            question="Full name"
                            answer={`${employee?.firstName} ${employee?.middleName} ${employee?.lastName}`}
                            questionFieldName={key}
                          />
                        );
                      }

                      if (underageOrSeniorKey === "dateOfBirth") {
                        const formattedDate = ConvertToValidDate(
                          underageOrSenior[underageOrSeniorKey]
                        );

                        return (
                          <Info
                            key={`${underageOrSeniorKey}-${key}-${index}`}
                            question={underageOrSeniorValue}
                            answer={formattedDate}
                            questionFieldName={key}
                          />
                        );
                      }

                      return (
                        <Info
                          key={`${underageOrSeniorKey}-${key}-${index}`}
                          question={underageOrSeniorValue}
                          answer={underageOrSenior[underageOrSeniorKey]}
                          questionFieldName={key}
                        />
                      );
                    }
                  )}

                  <hr className={styles.subsectionEnd} />
                </React.Fragment>
              );
            }
          );
        }

        return (
          <Info
            key={key}
            question={value}
            answer={data?.hasEmployees?.[key] ?? data?.bizActivities?.[key]}
            questionFieldName={key}
          />
        );
      })}
    </EditCard>
  );
};

export default EmployeesAttributesReview;
